import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/classnames'

import { HowItWorksStep } from './steps'

/** Start fetching while the panel is still this far below the viewport. */
const PRELOAD_MARGIN = '800px 0px'

type HowItWorksPanelProps = {
  steps: HowItWorksStep[]
  stepIndex: number
  paused: boolean
  runId: number
  labelledBy: string
  onNext: () => void
}

const HowItWorksPanel = ({ steps, stepIndex, paused, runId, labelledBy, onNext }: HowItWorksPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [loadImages, setLoadImages] = useState(false)
  const nextIndex = (stepIndex + 1) % steps.length

  useEffect(() => {
    const panel = panelRef.current
    if (!panel || loadImages) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setLoadImages(true)
        observer.disconnect()
      },
      { rootMargin: PRELOAD_MARGIN },
    )

    observer.observe(panel)
    return () => observer.disconnect()
  }, [loadImages])

  return (
    <div ref={panelRef} id='how-it-works-panel' role='tabpanel' aria-labelledby={labelledBy} className='mx-auto mt-8 w-full max-w-[480px] md:max-w-none'>
      <div className='mb-4 h-0.5 overflow-hidden rounded-full bg-white' aria-hidden='true'>
        <div key={`${stepIndex}-${runId}`} className={cn('h-full w-full origin-left bg-[#F3603C]', !paused && 'animate-how-it-works-progress')} style={paused ? { transform: 'scaleX(0)' } : undefined} />
      </div>
      <button
        type='button'
        onClick={onNext}
        aria-label={`${nextIndex + 1}. ${steps[nextIndex].title}`}
        className='grid w-full cursor-pointer rounded-3xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F3603C]'
      >
        {steps.map((step, index) => (
          <img
            key={step.image}
            src={loadImages ? step.image : undefined}
            decoding='async'
            alt={index === stepIndex ? step.description : ''}
            width={1312}
            height={1199}
            aria-hidden={index !== stepIndex}
            className={cn(
              // Phones crop to the handset so its screen stays readable. Uses an arbitrary property because the `aspect-*` utilities are disabled in tailwind.config.ts.
              'col-start-1 row-start-1 h-auto w-full overflow-hidden rounded-3xl object-cover object-[47%_50%] [aspect-ratio:5/9] sm:[aspect-ratio:3/4] md:[aspect-ratio:1312/1199]',
              index === stepIndex ? 'z-10 opacity-100 motion-safe:animate-how-it-works-in' : 'pointer-events-none opacity-0',
            )}
          />
        ))}
      </button>
    </div>
  )
}

export default HowItWorksPanel
